import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Shield, AlertTriangle, Lock, Unlock, RefreshCw } from 'lucide-react';
import { bruneiFormatLongTs } from '../../lib/brunei';

export default function SecuritySettings() {
  const [policies, setPolicies] = useState([]);
  const [globalOverride, setGlobalOverride] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API}/api/admin/security/mfa-policy`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load MFA policies');
      }

      const data = await response.json();
      setPolicies(data.policies || []);
      setGlobalOverride(data.global_override || null);
    } catch (error) {
      console.error('Error loading MFA policies:', error);
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePolicy = async (roleName, currentValue) => {
    const newValue = !currentValue;
    setUpdating(roleName);

    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API}/api/admin/security/mfa-policy/${roleName}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mfa_enabled: newValue })
      });

      // Parse response only once
      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(result.detail || result.message || 'Failed to update policy');
      }

      toast.success(result.message || 'Policy updated successfully');
      
      // Reload policies to get latest state
      await loadPolicies();
    } catch (error) {
      console.error('Error updating MFA policy:', error);
      toast.error(error.message || 'Failed to update MFA policy');
    } finally {
      setUpdating(null);
    }
  };

  const getRoleIcon = (roleName) => {
    switch (roleName) {
      case 'admin':
        return '👑';
      case 'manager':
        return '📋';
      case 'staff':
        return '👤';
      default:
        return '🔐';
    }
  };

  const getRoleDescription = (roleName) => {
    switch (roleName) {
      case 'admin':
        return 'Full system access - highest security level';
      case 'manager':
        return 'Manage bookings, pricing, and locations';
      case 'staff':
        return 'Handle bookings and customer requests';
      default:
        return '';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return bruneiFormatLongTs(dateString);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading security settings...</p>
        </div>
      </div>
    );
  }

  const isDevelopmentMode = globalOverride && !globalOverride.mfa_enabled;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black font-heading uppercase text-foreground mb-2">
          Security Settings
        </h1>
        <p className="text-muted-foreground">
          Configure multi-factor authentication (MFA) requirements for admin roles
        </p>
      </div>

      {/* Global Override Banner */}
      {globalOverride && (
        <Card className={`p-6 ${isDevelopmentMode ? 'border-red-500 bg-red-50 dark:bg-red-950/20' : 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20'}`}>
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${isDevelopmentMode ? 'bg-red-100 dark:bg-red-900' : 'bg-emerald-100 dark:bg-emerald-900'}`}>
              {isDevelopmentMode ? (
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              ) : (
                <Shield className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              )}
            </div>
            
            <div className="flex-1">
              <h3 className={`text-lg font-bold mb-2 ${isDevelopmentMode ? 'text-red-900 dark:text-red-100' : 'text-emerald-900 dark:text-emerald-100'}`}>
                {isDevelopmentMode ? '⚠️ DEVELOPMENT MODE ACTIVE' : '🔒 PRODUCTION MODE ACTIVE'}
              </h3>
              <p className={`text-sm mb-4 ${isDevelopmentMode ? 'text-red-700 dark:text-red-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                {isDevelopmentMode 
                  ? 'MFA is currently DISABLED for ALL roles. This setting overrides individual role policies below. Use only for testing or emergency access.'
                  : 'MFA policies are enforced based on individual role settings below. This is the recommended configuration for production environments.'
                }
              </p>
              
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => handleTogglePolicy('global_override', globalOverride.mfa_enabled)}
                  disabled={updating === 'global_override'}
                  variant={isDevelopmentMode ? "destructive" : "default"}
                  size="sm"
                  className="gap-2"
                >
                  {updating === 'global_override' ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      {isDevelopmentMode ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                      {isDevelopmentMode ? 'Enable Production Mode' : 'Enable Development Mode'}
                    </>
                  )}
                </Button>
                
                {globalOverride.updated_by && globalOverride.updated_by !== 'system' && (
                  <div className="text-xs text-muted-foreground">
                    Last changed by: <span className="font-semibold">{globalOverride.updated_by}</span>
                    {globalOverride.updated_at && ` on ${formatDateTime(globalOverride.updated_at)}`}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* MFA Policy by Role */}
      <Card className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold font-heading flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            MFA Policy by Role
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            Control which roles require email OTP verification on login
            {isDevelopmentMode && ' (Currently overridden by Development Mode)'}
          </p>
        </div>

        <div className="space-y-4">
          {policies.map((policy) => {
            const isUpdating = updating === policy.role_name;
            const isEnabled = policy.mfa_enabled;

            return (
              <div
                key={policy.role_name}
                className={`p-4 rounded-xl border-2 transition-all ${
                  isEnabled
                    ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20'
                    : 'border-gray-200 bg-gray-50/50 dark:bg-gray-900/20'
                } ${isDevelopmentMode ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="text-4xl">{getRoleIcon(policy.role_name)}</div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-bold capitalize">{policy.role_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isEnabled
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                            : 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                        }`}>
                          {isEnabled ? '🔐 MFA Required' : '🔓 MFA Optional'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {getRoleDescription(policy.role_name)}
                      </p>
                      {policy.updated_by && policy.updated_by !== 'system' && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Last updated by <span className="font-semibold">{policy.updated_by}</span>
                          {policy.updated_at && ` on ${formatDateTime(policy.updated_at)}`}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Toggle Switch */}
                    <button
                      onClick={() => handleTogglePolicy(policy.role_name, policy.mfa_enabled)}
                      disabled={isUpdating || isDevelopmentMode}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isEnabled ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-gray-700'
                      }`}
                      title={isDevelopmentMode ? 'Disabled in Development Mode' : ''}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          isEnabled ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>

                    {isUpdating && (
                      <RefreshCw className="w-4 h-4 animate-spin text-primary" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Info Card */}
      <Card className="p-6 bg-blue-50 dark:bg-blue-950/20 border-blue-200">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 dark:text-blue-100">
            <p className="font-semibold mb-2">Security Best Practices</p>
            <ul className="space-y-1 text-blue-700 dark:text-blue-300">
              <li>• <strong>Admin</strong>: Should always require MFA (highest security)</li>
              <li>• <strong>Manager</strong>: Recommended to require MFA (handles sensitive data)</li>
              <li>• <strong>Staff</strong>: Optional MFA (convenience for front-desk staff)</li>
              <li>• <strong>Development Mode</strong>: Only enable for local testing, never in production</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
